import {
  Box,
  Button,
  Card,
  Divider,
  CircularProgress,
  Typography,
  Container,
  Fade,
  Skeleton,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useState, useEffect, useId } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

import GeneralButtonGrid from "./GeneralButtonGrid";
import ShiftRemarksButton from "./shiftRemarksButton";
import ViewJobPlan from "./ViewJobPlan";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LoadingSkeleton = () => {
  const baseId = useId();
  return (
    <Box sx={{ mt: 3 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton
          key={`${baseId}-${i}`}
          variant="rectangular"
          height={60}
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.06), // THEME-AWARE
            border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`, // THEME-AWARE
          }}
        />
      ))}
    </Box>
  );
};

export default function GeneralProcess() {
  const location = useLocation();
  const navigate = useNavigate();

  const [username] = useState(
    location.state?.username || sessionStorage.getItem("username") || ""
  );
  const [operation_code] = useState(
    location.state?.operation_code || sessionStorage.getItem("operation_code") || ""
  );
  const [operation_description] = useState(
    location.state?.operation_description ||
      sessionStorage.getItem("operation_description") ||
      ""
  );
  const [start_datetime] = useState(
    location.state?.start_datetime || sessionStorage.getItem("start_datetime") || ""
  );
  const employee_id = username; // ✅ username == employee_id

  const [machine_code, setMachine_code] = useState(
    location.state?.machine_code || sessionStorage.getItem("machine_code") || ""
  );
  const [jobCodes, setJobCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (location.state) {
      sessionStorage.setItem("username", location.state.username || "");
      sessionStorage.setItem("operation_code", location.state.operation_code || "");
      sessionStorage.setItem(
        "operation_description",
        location.state.operation_description || ""
      );
      sessionStorage.setItem("start_datetime", location.state.start_datetime || "");
      sessionStorage.setItem("machine_code", location.state.machine_code || "");
    }
  }, [location.state]);

  useEffect(() => {
    let redirectTimer;

    const ensureMachineCode = async () => {
      if (!machine_code && username) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/getMachineCode?username=${username}`
          );
          const resolvedCode = response.data?.machineCode;

          if (!resolvedCode) {
            throw new Error("No workstation assigned to this user.");
          }

          sessionStorage.setItem("machine_code", resolvedCode);
          setMachine_code(resolvedCode);
          setError(null);
        } catch (err) {
          console.error("Failed to recover machine_code via API:", err);

          const status = err?.response?.status;
          let message =
            "Failed to fetch workstation information. Redirecting to login...";

          if (status === 401 || status === 403) {
            message = "Session expired. Redirecting to login...";
          } else if (status === 404) {
            message =
              "Workstation not found for this user. Redirecting to login...";
          } else if (err?.message === "Network Error") {
            message = "Network error. Redirecting to login...";
          }

          setError(message);

          // Session data intentionally NOT cleared — login page's
          // alterUserLogin flow uses localStorage.username to resume sessions.
          redirectTimer = setTimeout(() => {
            navigate("/", { replace: true });
          }, 2500);
        }
      }
    };
    ensureMachineCode();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [machine_code, username, navigate]);

  useEffect(() => {
    let redirectTimer;

    const fetchJobCodes = async () => {
      // Guard: if username is missing (direct URL hit / session lost),
      // bounce straight to login.
      if (!username) {
        navigate("/", { replace: true });
        return;
      }

      try {
        setLoading(true);
        const [response] = await Promise.all([
          axios.get(`${API_BASE_URL}/GeneralProcess/${username}`),
          new Promise((resolve) => setTimeout(resolve, 200)),
        ]);
        setJobCodes(response.data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch job codes:", err);

        const status = err?.response?.status;
        let message =
          "Failed to fetch job codes. Redirecting to login...";

        if (status === 401 || status === 403) {
          message = "Session expired. Redirecting to login...";
        } else if (status === 404) {
          message = "Job codes not found for this user. Redirecting to login...";
        } else if (err?.message === "Network Error") {
          message = "Network error. Redirecting to login...";
        }

        setError(message);

        // Session data intentionally NOT cleared — login page's
        // alterUserLogin flow uses localStorage.username to resume sessions.
        redirectTimer = setTimeout(() => {
          navigate("/", { replace: true });
        }, 2500);
      } finally {
        setLoading(false);
      }
    };
    fetchJobCodes();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [username, navigate]);

  const handleBackToMainCategories = () => {
    navigate("/MainCategories", { state: { username } });
  };

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      alert(
        "You can't go back from this window-කණගාටුයි! ඔබට මෙම පේජ් එකෙන් පසුපසට යා නොහැක."
      );
    };
    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, document.title, window.location.href);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  return (
    <div className="banner">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Fade in timeout={800}>
          <Card
            elevation={8}
            sx={{
              // Glass panel that adapts to theme
              bgcolor: (t) => alpha(t.palette.background.paper, 0.96),
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
              backdropFilter: "blur(20px)",
              borderRadius: 4,
              overflow: "hidden",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: (t) =>
                  `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`, // THEME-AWARE
              },
            }}
          >
            <Box sx={{ p: 3, pb: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <IconButton
                  onClick={handleBackToMainCategories}
                  sx={{
                    color: (t) => t.palette.text.primary,
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
                    "&:hover": {
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.14),
                      transform: "translateX(-2px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <ShiftRemarksButton Employee_ID={username} />
                  <ViewJobPlan username={username} />
                </Box>
              </Box>

              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                  }}
                >
                  <PrecisionManufacturingIcon
                    sx={{
                      fontSize: 40,
                      mr: 2,
                      color: (t) => t.palette.primary.main,
                      filter: (t) =>
                        `drop-shadow(0 2px 4px ${alpha(t.palette.primary.main, 0.3)})`,
                    }}
                  />
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: "-0.5px",
                      background: (t) =>
                        `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    General Process
                  </Typography>
                </Box>

                {machine_code && (
                  <Chip
                    label={machine_code}
                    icon={<PrecisionManufacturingIcon />}
                    variant="outlined"
                    sx={{
                      bgcolor: (t) => t.palette.background.paper,
                      color: (t) => t.palette.text.primary,
                      borderColor: (t) => alpha(t.palette.primary.main, 0.35),
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      px: 1,
                      "& .MuiChip-icon": {
                        color: (t) => t.palette.primary.main,
                      },
                    }}
                  />
                )}
              </Box>

              <Divider sx={{ bgcolor: (t) => t.palette.divider, height: 2, borderRadius: 1 }} />
            </Box>

            <Box sx={{ p: 3, pt: 2 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    border: (t) => `1px solid ${alpha(t.palette.error.main, 0.3)}`,
                  }}
                >
                  {error}
                </Alert>
              )}
              {loading || !machine_code ? (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 3,
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <CircularProgress
                      size={48}
                      thickness={4}
                      sx={{
                        color: (t) => t.palette.primary.main, // THEME-AWARE
                        filter: (t) =>
                          `drop-shadow(0 2px 4px ${alpha(t.palette.primary.main, 0.25)})`,
                      }}
                    />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {machine_code ? "Loading job codes..." : "Fetching machine code..."}
                    </Typography>
                  </Box>
                  <LoadingSkeleton />
                </Box>
              ) : (
                <Fade in timeout={600}>
                  <Box>
                    <GeneralButtonGrid
                      jobCodes={jobCodes}
                      username={username}
                      operation_code={operation_code}
                      operation_description={operation_description}
                      start_datetime={start_datetime}
                      machine_code={machine_code}
                      employee_id={employee_id}
                    />
                  </Box>
                </Fade>
              )}
            </Box>

            <Box sx={{ p: 3, pt: 0, textAlign: "center" }}>
              <Button
                onClick={handleBackToMainCategories}
                variant="contained"
                size="large"
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "common.white",
                  px: 4,
                  py: 1.5,
                  backgroundImage: (t) =>
                    `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                  boxShadow: (t) => `0 6px 20px ${alpha(t.palette.primary.main, 0.4)}`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: (t) => `0 8px 25px ${alpha(t.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                Back to Main
              </Button>
            </Box>
          </Card>
        </Fade>
      </Container>
    </div>
  );
}